import React, { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useTheme } from '~/ui/styled'
import { Keyboard, Platform } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import useNavigation from "~hooks/useNavigation"
import { PrivacyModal } from "~modals/privacy-modal"
import { ServiceContractModal } from "~modals/service-contract-modal"
import { ColumnFlex, CenterFlex, BetweenFlex, Text, Button } from "~components/El"
import { AuthLayout } from "~components/Layout"
// import { alertError } from "~utils/alert"
import { globalToast } from "~utils"
import { loadString, saveString } from "~utils/storage"
import { validate, isEmpty, rules } from "~utils/validator"
import { useStores } from "~models"
import { usePopup } from "~hooks/usePopup"
import { hideContact } from "~utils"
import { normalizeAuthSession } from "~utils/auth-session"
import Username from "./components/username"
import Password from "./components/password"
import SecurityVerificationPopup from "./components/security-verification-popup"
import GoogleVerificationPopup from "./components/google-verification-popup"
import { CheckBox } from "~widgets/check-box"
//登录表单
const Login: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const insets = useSafeAreaInsets()
  const { authStore, apiStore, configStore, walletStore } = useStores()
  const popup = usePopup()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [formErr, setFormErr] = useState(false)
  const [remember, setRemember] = useState(false)
  // const [isAgreePrivacy, setIsAgreePrivacy] = useState(authStore.haveBeenPassPrivacy)
  const [isServiceContractModalVisible, setIsServiceContractModalVisible] = useState(false)
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false)
  const [keyboardVisible, setKeyboardVisible] = useState(false)
  const onLogin = async () => {
    setLoading(true)
    try {
      const account = username.trim()
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account)
      const isPhone = /^\+[1-9]\d{1,14}$/.test(account) || /^1[3-9]\d{9}$/.test(account)

      const payload = isEmail
        ? { email: account, password }
        : isPhone
          ? { phone: account, password }
          : { username: account, password }

      const data = await apiStore.login(payload)
      // console.log("data", data)
      if (data) {
        if (data.data?.require_device_verify) {
          const maskedContact = hideContact(
            isPhone ? account : isEmail ? account : account,
          )
          popup.open({
            type: "center",
            content: () =>
              React.createElement(SecurityVerificationPopup, {
                maskedContact,
                isEmail,
                account,
                onVerify: async (verifyCode) => {
                  try {
                    const next = await apiStore.verifyNewDeviceLogin({
                      challenge_token: data.data?.challenge_token,
                      code: verifyCode,
                    })
                    if (!normalizeAuthSession(next).token) {
                      throw new Error("验证失败")
                    }
                    await authStore.login(next)
                    walletStore.getWalletInfo()
                    authStore.setHaveBeenPrivacy(true)
                    popup.close()
                  } catch (error) {
                    globalToast.show(error?.message || "验证失败")
                  }
                }
              }),
          })
        } else if (data.data?.require_2fa) {
          // 需要双重验证
          popup.open({
            type: "center",
            content: () =>
              React.createElement(GoogleVerificationPopup, {
                onVerify: async (verifyCode) => {
                  try {
                    const next = await apiStore.verifyGoogleAuthLogin({
                      twofa_token: data.data?.twofa_token,
                      code: verifyCode,
                    })
                    if (!normalizeAuthSession(next).token) {
                      throw new Error("验证失败")
                    }
                    await authStore.login(next)
                    walletStore.getWalletInfo()
                    authStore.setHaveBeenPrivacy(true)
                    if (remember) {
                      await saveString("u", username)
                      await saveString("p", password)
                    } else {
                      await saveString("u", "")
                      await saveString("p", "")
                    }
                    popup.close()
                  } catch (error) {
                    globalToast.show(error?.message || "验证失败")
                  }
                  
                  
                }
              }),
          })
        } else {
          await authStore.login(data)
          walletStore.getWalletInfo()
          authStore.setHaveBeenPrivacy(true)
          if (remember) {
            await saveString("u", username)
            await saveString("p", password)
          } else {
            await saveString("u", "")
            await saveString("p", "")
          }
        }
        
      } else {
        globalToast.show("登录失败")
      }
    } catch (e) {
      // alertError({ message: e.message })
      globalToast.show(e?.message || "登录失败")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const preload = async () => {
      const u = await loadString("u")
      const p = await loadString("p")
      if (u) {setUsername(u)}
      if (p) {
        setPassword(p)
        setRemember(true)
      }
    }
    preload()
  }, [])

  useEffect(() => {
    setFormErr(
      !isEmpty(
        validate(
          { username: rules.account, password: rules.password },
          { username, password },
        ),
      ),
    )
  }, [username, password])

  useEffect(() => {
    const showSub = Keyboard.addListener("keyboardDidShow", () => setKeyboardVisible(true))
    const hideSub = Keyboard.addListener("keyboardDidHide", () => setKeyboardVisible(false))
    return () => {
      showSub.remove()
      hideSub.remove()
    }
  }, [])
  return (
    <AuthLayout
      title="欢迎回来"
      subtitle="登录您的账户继续使用"
      onSubmit={() => {
        if (!formErr && !loading) void onLogin()
      }}
    >
      <ColumnFlex width="100%" height="100%" padding="24px 20px">
        <Username value={username} onChange={setUsername} />
        <Password value={password} onChange={setPassword} />
        <BetweenFlex width="100%" mt="10px">
          <CheckBox
            label="记住密码"
            curValue={remember}
            value={true}
            onClick={() => setRemember(!remember)}
          />
          <Button onClick={() => { navigation.navigate("ForgetPassword") }}>
            <Text color={theme.colors.primaryText} fontSize={theme.fontSizes.md}>忘记密码?</Text>
          </Button>
        </BetweenFlex>

        <CenterFlex width="100%" mt="8px">
          <Button
            width="100%"
            height="48px"
            borderRadius="8px"
            bg={theme.colors.primary}
            loading={loading}
            disabled={formErr}
            loadingText="登录中"
            onClick={onLogin}
          >
            <Text color={theme.colors.textLight} fontSize={theme.fontSizes.lg} fontWeight="600">
              登录
            </Text>
          </Button>
        </CenterFlex>
        <CenterFlex width="100%" mt="24px" flexDirection="row">
          <Button flexDirection="row" onClick={() => { navigation.navigate("Register") }}>
            <Text color={theme.colors.text} fontSize={theme.fontSizes.md}>还没有账户？</Text>
            <Text color={theme.colors.primaryText} fontSize={theme.fontSizes.md}>&nbsp;立即注册</Text>
          </Button>
        </CenterFlex>
        {(Platform.OS === "web" || !keyboardVisible) && (
          <CenterFlex
            position={Platform.OS === "web" ? "relative" : "absolute"}
            left={Platform.OS === "web" ? undefined : "20px"}
            bottom={Platform.OS === "web" ? undefined : insets.bottom}
            width="100%"
            mt={Platform.OS === "web" ? "32px" : undefined}
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="center"
          >
            <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md}>登录即表示您同意我们的</Text>
            <Button onClick={() => { setIsServiceContractModalVisible(true)}}>
              <Text color={theme.colors.primaryText} fontSize={theme.fontSizes.md}>&nbsp;服务条款</Text>
            </Button>
            <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md}>和</Text>
            <Button onClick={() => { setIsPrivacyModalVisible(true) }}>
              <Text color={theme.colors.primaryText} fontSize={theme.fontSizes.md}>&nbsp;隐私政策</Text>
            </Button>
          </CenterFlex>
        )}
      </ColumnFlex>
         
      <ServiceContractModal
        appName={configStore.appName}
        visible={isServiceContractModalVisible}
        setVisible={setIsServiceContractModalVisible}
       />
      <PrivacyModal
        appName={configStore.appName}
        visible={isPrivacyModalVisible}
        setVisible={setIsPrivacyModalVisible}
       />
    </AuthLayout>
  )
}
export default observer(Login)
